import { observer } from 'mobx-react'
import React from 'react'
import usePortManager from '../hooks/use-port-manager'
import ArrowLinkBox from '../misc/ArrowLinkBox'
import Modal from 'react-modal'
import InputWrapper from '../inputs/InputWrapper'
import { action, computed, observable } from 'mobx'
import { mttAjax } from '../../../utils/_mtt-ajax'
import { LocalizedInput } from '../order/LocalizedInput'
import useSettingsManager from '../hooks/use-settings-manager'
import PropTypes from 'prop-types'
import Spinner from '../misc/Spinner'

class DrawingState {
  @observable accessor first_name = ''
  @observable accessor last_name = ''
  @observable accessor email = ''
  @observable accessor phone = ''
  @observable accessor portSelection = new Map()
  @observable accessor success_message = null
  @observable accessor error_message = null
  @observable accessor is_loading = false
  @observable accessor is_sent = false

  @action setValue(key, value) {
    this[key] = value
  }

  @computed get ports () {
    let data = window.localStorage.getItem('ports')
    try {
      data = JSON.parse(data)
      return typeof data === 'object' && data !== null ? data : []
    } catch (e) {
      return []
    }
  }

  @computed get selectedPorts () {
    let ports = this.ports
    return ports.filter(p => {
      return this.isPortSelected(p.port_id)
    })
  }

  toJson (drawingType) {
    return {
      f_name: this.first_name,
      email: this.email,
      phone: this.phone,
      l_name: this.last_name,
      ports: this.selectedPorts,
      drawing_type: drawingType
    }
  }

  @action
  submit (drawingType) {
    this.success_message = null
    this.error_message = null
    this.is_loading = true
    let data = this.toJson(drawingType)
    mttAjax(
      'submit_drawing',
      { action: 'submit_drawing', data: data },
      r => {
        let m = typeof r.data.message === 'string' ? r.data.message : null
        if (r.success === true) {
          ga('send','event', 'submit_' + data.drawing_type,'submit')
          this.setValue('is_sent', true)
          this.setValue('success_message', m)
        } else {
          this.setValue('error_message', m)
        }
        this.setValue('is_loading', false)
      }
    )
  }

  @action togglePort (port_id) {
    let selected = this.isPortSelected(port_id)
    this.portSelection.set(port_id, !selected)
  }

  isPortSelected (port_id) {
    if (!this.portSelection.has(port_id)) {
      return true
    }
    return Boolean(this.portSelection.get(port_id))
  }
}

const drawingState = new DrawingState()
export const DrawingModal = observer(function DrawingModal ({
  drawingTitle,
  drawingDescription,
  drawingType
}) {
  const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      width: 'auto'
    }
  }
  const [modalIsOpen, setModalIsOpen] = React.useState(false)

  const closeModal = React.useCallback(() => {
    setModalIsOpen(false)
    drawingState.setValue('is_loading', false)
    drawingState.setValue('is_sent', false)
    drawingState.setValue('success_message', null)
    drawingState.setValue('error_message', null)
  }, [setModalIsOpen])

  const openModal = React.useCallback(() => setModalIsOpen(true), [
    setModalIsOpen
  ])

  const portManager = usePortManager()

  const settings_manager = useSettingsManager()

  return (
    <React.Fragment>
      <ArrowLinkBox
        onClick={openModal}
        title={drawingTitle}
        description={drawingDescription}
      />
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel={drawingTitle}
        style={customStyles}
      >
        <div id={'port-configurator'} className={'port-drawing'}>
          <form
            className={'final-step'}
            onSubmit={e => {
              e.preventDefault()
              drawingState.submit(drawingType)
            }}
          >
            {drawingState.success_message ? (
              <div
                style={{ marginTop: '12px' }}
                dangerouslySetInnerHTML={{
                  __html: drawingState.success_message
                }}
              />
            ) : (
              <>
                <InputWrapper name={drawingTitle}>
                  <aside
                    className={'input input-text input-box'}
                    data-size="full"
                  >
                    <div className={'input-box__title'}>
                      {settings_manager.getOtherSettings(
                        'drawing_port_selection',
                        'Vælg hvilke Porte du vil have tegning på'
                      )}
                    </div>
                    <div className={'port-overview-nav'}>
                      {portManager.ports.map((port, index) => (
                        <button
                          type={'button'}
                          key={index}
                          onClick={() => drawingState.togglePort(port.port_id)}
                          data-active={drawingState.isPortSelected(
                            port.port_id
                          )}
                        >
                          {port.name}
                        </button>
                      ))}
                    </div>
                  </aside>

                  <LocalizedInput
                    name="first_name"
                    settingsKey="drawing_first_name"
                    value="Fornavn"
                    size="half"
                    stateObj={drawingState}
                    required
                  />
                  <LocalizedInput
                    name="last_name"
                    settingsKey="drawing_last_name"
                    value="Efternavn"
                    size="half"
                    stateObj={drawingState}
                    required
                  />
                  <LocalizedInput
                    name="email"
                    settingsKey="drawing_email"
                    value="Email"
                    size="full"
                    type="email"
                    stateObj={drawingState}
                    required
                  />
                  <LocalizedInput
                    name="phone"
                    settingsKey="drawing_phone"
                    value="Telefon"
                    size="full"
                    type="tel"
                    stateObj={drawingState}
                    required
                  />
                  {drawingState.is_loading ? (
                    <div className={'input-box'}>
                      <Spinner/>
                    </div>
                  ) : (
                    <div className={'input-box'} data-size="full">
                      <button
                        className={'button primary'}
                        type={'submit'}
                        disabled={
                          drawingState.is_sent || drawingState.is_loading
                        }
                      >
                        OK
                      </button>
                    </div>
                  )}
                </InputWrapper>
                {drawingState.error_message ? (
                  <div
                    style={{ marginTop: '12px' }}
                    dangerouslySetInnerHTML={{
                      __html: drawingState.error_message
                    }}
                  />
                ) : null}
              </>
            )}
          </form>
        </div>
      </Modal>
    </React.Fragment>
  )
})
DrawingModal.propTypes = {
  drawingTitle: PropTypes.string.isRequired,
  drawingDescription: PropTypes.string.isRequired,
  drawingType: PropTypes.string.isRequired
}
